import {
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectBalances,
  ConnectPayouts,
} from '@stripe/react-connect-js';
import { useQueryClient } from '@tanstack/react-query';

import { Card } from '../../components/ui/Card';
import { useDocumentTitle } from '../../lib/title';
import { useStripeStatus } from './connect';
import { EarningsPanel } from './EarningsPanel';

/**
 * The three pages under `/earnings`.
 *
 * Each is one embedded component and nothing else, because each is an address
 * Stripe was given for exactly that component - see `EarningsLayout` for why
 * they are routes rather than tabs. The provider is the layout's; a page here
 * never builds a Connect instance of its own.
 */

/**
 * Where the host stands, in a sentence.
 *
 * Stripe's own component says what is outstanding in detail; this exists so the
 * answer to "can I be paid yet" is legible without reading a form.
 */
function Ready({
  payoutsEnabled,
  detailsSubmitted,
  requirements,
}: {
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirements: string[];
}) {
  if (payoutsEnabled) {
    return (
      <p className="text-sm text-soft">
        <span className="font-medium text-fresh">Ready.</span> Tips and boosts will be paid to
        your Stripe account. Details below if you need to change anything.
      </p>
    );
  }

  const count = requirements.length;

  return (
    <p className="text-sm text-soft">
      <span className="font-medium text-hot-text">Not finished.</span>{' '}
      {detailsSubmitted
        ? 'Stripe is reviewing your details.'
        : `Stripe needs ${count === 0 ? 'a few more details' : `${count} more ${count === 1 ? 'detail' : 'details'}`} before you can be paid.`}{' '}
      You can complete it below.
    </p>
  );
}

export function EarningsSummary() {
  const queryClient = useQueryClient();
  const { data } = useStripeStatus();

  return (
    <>
      <Card className="p-6" data-payouts-panel>
        <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-faint">Account</h2>

        {data && (
          <Ready
            payoutsEnabled={data.payouts_enabled}
            detailsSubmitted={data.details_submitted}
            requirements={data.requirements}
          />
        )}

        <div className="mt-4" data-connect-account>
          {data?.payouts_enabled ? (
            <ConnectAccountManagement />
          ) : (
            <ConnectAccountOnboarding
              onExit={() => {
                void queryClient.invalidateQueries({ queryKey: ['stripe', 'status'] });
              }}
            />
          )}
        </div>
      </Card>

      {/*
        Our own statement, beneath Stripe's. The two answer one question between
        them - "can I be paid" and "have I been" - and a host who has just
        finished onboarding is looking for the second within a day.
      */}
      <EarningsPanel />
    </>
  );
}

export function EarningsPayouts() {
  useDocumentTitle('Payouts');

  return (
    <Card className="p-6" data-connect-payouts>
      <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-faint">Payouts</h2>
      <ConnectPayouts />
    </Card>
  );
}

export function EarningsBalances() {
  useDocumentTitle('Balances');

  return (
    <Card className="p-6" data-connect-balances>
      <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-faint">Balances</h2>
      <ConnectBalances />
    </Card>
  );
}
